import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import renderHTML from "react-render-html";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
    withStyles
} from '@material-ui/core';
import { createHistory, formatDate, hasAtLeastOnePermission, hasPermission, parseURL, toHex } from '../../sc-utilities';
import BinaryImageList from './BinaryImageList';
import * as actions from "./store/binaryImageActions";
import { download, externalDownload } from '../../sc-utilities/download.utils';
import { CloudDownload, FindInPage, Notes, Send } from '@material-ui/icons';
import { CancelToken } from 'axios';
import config from "../../../config/config";
import { snackActions } from "../../sc-utilities/SnackBar";

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='inactive']": {
            color: theme.palette.error.dark
        },
        "[class*='minor']": {
            color: theme.palette.error.light
        },
        "[class*='normal']": {
            color: theme.palette.success.dark
        },
        "[class*='urgent']": {
            color: theme.palette.error.main
        }
    },
    inactive: {
        color: theme.palette.error.dark
    },
    minor: {
        color: theme.palette.error.light
    },
    normal: {
        color: theme.palette.success.dark
    },
    urgent: {
        color: theme.palette.error.main
    }
});

const BinaryImages = props => {
    const tableRef = useRef();
    const queryParams = parseURL(window.location.href);
    const { classes, columns, translations, permissions, activeCustomerID, onLoadBinaryImage, onSendReleaseNotes, binaryImages, onFetchBinaryImageTypes } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, binaryImageID: null, redirectPath: '' });
    const [releaseModalState, setReleaseModalState] = useState({ open: false, binaryImageID: null, name: "" });
    const [binaryImageTypes, setBinaryImageTypes] = useState(null);

    let visible = columns && columns['BinaryImages'];

    const handleDownload = async binaryImage => {
        let objectURL = null;
        let exportDataSuccessful = null;
        switch (binaryImage.Type) {
            case 'Internal': {
                objectURL = `${config.backend_domain}:${config.backend_port}/binary-images/download?BinaryImageID=${binaryImage.BinaryImageID}&ActiveCustomerID=${activeCustomerID}`;
                exportDataSuccessful = await download(objectURL);
                break;
            }
            case 'External': {
                exportDataSuccessful = externalDownload(binaryImage?.URL);
                break;
            }
            case 'External-AzureBlob': {
                objectURL = `${config.backend_domain}:${config.backend_port}/azure-blobs/download?Name=${binaryImage?.URL?.split('download/')?.[1]}`;
                exportDataSuccessful = await download(objectURL);
                break;
            }
            default:
                break;
        }
        tableRef.current && tableRef.current.onQueryChange();
        if (!exportDataSuccessful) {
            return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
        }
    };

    const showReleaseNotes = async (binaryImageID, name) => {
        await onLoadBinaryImage({ BinaryImageID: binaryImageID, ActiveCustomerID: activeCustomerID });
        setReleaseModalState({ open: true, binaryImageID: binaryImageID, name: name });
    };
    const sendReleaseNotes = async event => {
        let binaryImageID = event.currentTarget.value;
        await onSendReleaseNotes({ BinaryImageID: binaryImageID, ActiveCustomerID: activeCustomerID });
    };

    useEffect(() => {
        const requestToken = CancelToken.source();
        const fetchBinaryImageTypes = async () => {
            let binaryImageTypeData = await onFetchBinaryImageTypes(activeCustomerID, { CancelToken: requestToken.token });
            let formattedBinaryImageTypes = binaryImageTypeData.reduce(
                (obj, item) => Object.assign(obj, { [item.value]: item.displayValue }), {});
            setBinaryImageTypes(formattedBinaryImageTypes);
        };
        fetchBinaryImageTypes();
        return () => {
            requestToken.cancel('Operation canceled by the user.');
        };
    }, [onFetchBinaryImageTypes, activeCustomerID]);

    const formatBuildType = buildType => {
        switch (buildType) {
            case "prod":
                return translations["prod"] || 'Production';
            case "test":
                return translations["test"] || "Test";
            case "beta":
                return translations["beta"] || 'Beta';
            case "internal":
                return translations["internal"] || 'Internal';
            default:
                return "test";
        }
    };

    const formatType = type => {
        switch (type) {
            case "Internal":
                return translations["internal_binary_image"] || 'Internal';
            case "External":
                return translations["external_binary_image"] || "External";
            case "External-AzureBlob":
                return translations["azure_binary_image"] || 'Azure';
            default:
                return "-";
        }
    };



    const getSeverity = severity => {
        switch (severity) {
            case 0:
                return <CustomChip key="inactive" className="inactive" value={translations['inactive'] || 'Inactive'} />;
            case 1:
                return <CustomChip key="minor" className="minor" value={translations['minor'] || 'Minor'} />;
            case 2:
                return <CustomChip key="normal" className="normal" value={translations['priority_normal'] || 'Normal'} />;
            case 3:
                return <CustomChip key="urgent" className="urgent" value={translations['urgent'] || 'Urgent'} />;
            default:
                return <CustomChip key="inactive" className="inactive" value={translations['inactive'] || 'Inactive'} />;
        }
    };

    const isValid = data => {
        return (data !== null && data !== undefined)
    };

    const tableColumns = useMemo(() => {
        return [{
            title: translations["action"] || "Action", field: 'BinaryImageID',
            hidden: visible ? visible['BinaryImageID'] : false,
            cellStyle: { minWidth: 210, maxWidth: 210 },
            filtering: false,
            render: rowData => <>
                {hasPermission(permissions, 'binary_image_load') ?
                    <Tooltip key={`Edit-${rowData.BinaryImageID}`} title={translations["binary_image_details"] || "Software details"}>
                        <IconButton
                            id={`edit_binary_image_${rowData.BinaryImageID}`}
                            component='div'
                            onClick={(event) => redirectHandler(`/downloads/binary-images/edit?binaryImageID=${rowData.BinaryImageID}`)}
                        >
                            <FindInPage />
                        </IconButton>
                    </Tooltip>
                    : null}
                {hasPermission(permissions, "binary_image_download") ? (
                    <Tooltip
                        component="div"
                        title={
                            translations["download_image"] || "Download binary image"
                        }
                    >
                        <IconButton
                            id={`download_binary_image_${rowData.BinaryImageID}`}
                            value={rowData.BinaryImageID}
                            aria-label={translations["download_image"] || "Download binary image"}
                            onClick={() => handleDownload(rowData)}
                        >
                            <CloudDownload />
                        </IconButton>
                    </Tooltip>
                ) : null}
                <Tooltip title={translations["show_release_notes"] || "Show release notes"}>
                    <IconButton
                        id={`binary_image_release_notes_${rowData.BinaryImageID}`}
                        value={rowData.BinaryImageID}
                        aria-label={translations["show_release_notes"] || "Show release notes"}
                        onClick={() => showReleaseNotes(rowData.BinaryImageID, rowData.Name)}
                    >
                        <Notes />
                    </IconButton>
                </Tooltip>
                {hasPermission(permissions, "send_release_notes") ? (
                    <Tooltip title={translations["send_release_notes"] || "Send release notes"}>
                        <IconButton id={`send_release_notes_${rowData.BinaryImageID}`} value={rowData.BinaryImageID} aria-label={translations["send_release_notes"] || "Send release notes"} onClick={sendReleaseNotes}>
                            <Send />
                        </IconButton>
                    </Tooltip>
                ) : null}
            </>
        },
        {
            title: translations["image_type"] || 'Image type', field: 'BinaryImageTypeID',
            hidden: visible ? visible['BinaryImageTypeID'] : false,
            filtering: true,
            lookup: binaryImageTypes,
            defaultFilter: queryParams?.BinaryImageTypes ? queryParams?.BinaryImageTypes.split(",") : Object.keys(binaryImageTypes ?? {}),
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.BinaryImageType ? rowData.BinaryImageType.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["name"] || 'Name', field: 'Name',
            hidden: visible ? visible['Name'] : false,
            filtering: true,
            defaultFilter: queryParams?.Name ?? null,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["version"] || 'Version', field: 'Version',
            hidden: visible ? visible['Version'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{isValid(rowData.MajorVersion) && isValid(rowData.MinorVersion) && isValid(rowData.BuildNumber) ? `${rowData.MajorVersion}.${rowData.MinorVersion}.${rowData.BuildNumber}` : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["build_type"] || 'Build type', field: 'BuildType',
            hidden: visible ? visible['BuildType'] : false,
            filtering: true,
            lookup: { 'beta': translations["beta"] || 'Beta', 'prod': translations["prod"] || 'Production', 'internal': translations["internal"] || 'Internal' },
            defaultFilter: ['beta', 'prod', 'internal'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.BuildType ? formatBuildType(rowData.BuildType) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["binary_image_download_type"] || 'Download Type', field: 'Type',
            hidden: visible ? visible['Type'] : false,
            filtering: true,
            lookup: { 'Internal': translations["internal_binary_image"] || 'Internal', 'External': translations["external_binary_image"] || 'External', 'External-AzureBlob': translations["azure_binary_image"] || 'Azure' },
            defaultFilter: ['Internal', 'External', 'External-AzureBlob'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Type ? formatType(rowData.Type) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["crc32"] || 'CRC32', field: 'CRC',
            hidden: visible ? visible['CRC'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.CRC ? toHex(rowData.CRC) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["public"] || 'Public', field: 'Public',
            hidden: visible ? visible['Public'] : false,
            filtering: true,
            lookup: { '1': translations["public"] || 'Public', '0': translations["private"] || 'Private' },
            defaultFilter: ['1', '0'],
            render: rowData => {
                return <CustomChip key={`public_${rowData.BinaryImageID}`} className={rowData.Public ? 'normal' : 'inactive'} value={rowData.Public ? translations["public"] || "Public" : translations["private"] || "Private"} />;
            }
        },
        {
            title: translations["severity"] || 'Severity', field: 'Severity',
            hidden: visible ? visible['Severity'] : false,
            filtering: true,
            lookup: { '0': translations['inactive'] || 'Inactive', '1': translations['minor'] || 'Minor', '2': translations['priority_normal'] || 'Normal', '3': translations['urgent'] || 'Urgent' },
            defaultFilter: ['0', '1', '2', '3'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Severity !== null ? getSeverity(rowData.Severity) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["created_at"] || 'Created at', field: 'CreateAt',
            hidden: visible ? visible['CreateAt'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.CreatedAt ? formatDate(rowData.CreatedAt) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        }
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible, binaryImageTypes])

    if (!hasAtLeastOnePermission(permissions, [
        "binary_image_load"
    ])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, binaryImageID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, binaryImageID: binaryImageID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'binary_image_list') && binaryImageTypes ?
                <BinaryImageList
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                    tableRef={tableRef}
                />
                :
                null}
            <Dialog
                open={releaseModalState.open}
                onClose={() => setReleaseModalState({ ...releaseModalState, open: false, binaryImageID: null })}
                aria-labelledby="form-dialog-title"
                disableBackdropClick={true}
                disableEscapeKeyDown={false}
                fullWidth={true}
            >
                <DialogTitle id="form-dialog-title">    {translations['release_notes'] || 'Release notes'} - {releaseModalState.name} </DialogTitle>
                <DialogContent>
                    <DialogContentText>{binaryImages && binaryImages.loaded && binaryImages.loaded.ReleaseNotes ? renderHTML(binaryImages.loaded.ReleaseNotes) : ""}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        id="release_notes_close"
                        className={classes.buttonSafe}
                        onClick={() => setReleaseModalState({ ...releaseModalState, open: false, binaryImageID: null })}
                        variant="contained"
                    >
                        {translations['close'] || 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const CustomChip = props => {
    const { keyValue, value, className } = props;
    return (
        <Typography key={keyValue} className={className} variant={'caption'}>{value}</Typography>
    );
};

const mapStateToProps = state => {

    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
        binaryImages: state.exportImport.binaryImages,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onLoadBinaryImage: filter => dispatch(actions.loadBinaryImage(filter)),
        onFetchBinaryImageTypes: (activeCustomerID, options) => dispatch(actions.listBinaryImageTypes(activeCustomerID, options)),
        onSendReleaseNotes: id => dispatch(actions.sendReleaseNotes(id)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(BinaryImages));